/**
 * Completion-rail composition engine — Option B, LOCKED 2026-08-11
 * (docs/superpowers/sdlc/scheduling-ai/06-decisions/2026-08-11-completion-rail-architecture.md).
 *
 * GENERATIVE COMPOSITION OVER A LOCKED CARD VOCABULARY: this module decides
 * WHICH cards render, in what ORDER, with what STATE — per use case, from the
 * parsed draft plus what actually resolved. It is pure and deterministic so the
 * 31-row corpus (04-stories/completion-rail-corpus.json) asserts sequences
 * directly. The cards themselves are Signal-DS primitives; nothing here emits
 * UI. When PROD-1316's intent service replaces the stub parser, this engine is
 * unchanged — it consumes the same draft contract.
 *
 * Composition rules (the brief, verbatim):
 *  1. Order: RequestHeader → Patient → (Register?) → Practitioner → VisitType →
 *     Time | Recurrence | ChainLegs → Receipt → Book.
 *  2. Exactly ONE card is `open` (asking); resolved cards above are `done`;
 *     cards below are `pending`.
 *  3. A card renders ONLY when its use case demands it.
 *  4. Answering advances the open state to the next unresolved card.
 *  5. Book enables when nothing is unresolved — the human always fires it.
 *  6. Editing an answered card reopens it; doctor change resets visit + time
 *     (dependency knowledge lives with the consumer via `dependsOnDoctor`).
 */

import type {
  SchedulingCommandDraft,
} from '../scheduling-command-stub-parser';

export type CompletionCardKind =
  | 'request-header'
  | 'patient'
  | 'register-patient'
  | 'practitioner'
  | 'visit-type'
  | 'time-slot'
  | 'recurrence-dates'
  | 'chain-leg'
  | 'conflict'
  | 'receipt';

export type CompletionCardState = 'done' | 'open' | 'pending';

export interface CompletionCard {
  kind: CompletionCardKind;
  state: CompletionCardState;
  /** Stable key (chain legs get an index suffix). */
  key: string;
  /** Plain-words primary line the card renders (content, not markup). */
  label: string;
  /** Resets when the doctor changes (rule 6). */
  dependsOnDoctor?: boolean;
  /** Chain legs only — 0-based leg index after the first visit. */
  legIndex?: number;
}

/**
 * What the app has RESOLVED so far — the second input besides the draft.
 * The frame owns resolution (patient search, roster match, profile match,
 * slot pick); the engine only sequences.
 */
export interface CompletionResolution {
  /** 'resolved' = one patient picked; 'ambiguous' = >1 match; 'none' = zero matches; 'unsearched' = query not yet run. */
  patient: 'resolved' | 'ambiguous' | 'none' | 'unsearched';
  practitionerResolved: boolean;
  visitTypeResolved: boolean;
  /** Slot actually committed to form state (slotStart+slotEnd). */
  slotResolved: boolean;
  /** Recurring only: occurrence dates resolved via the configurator. */
  recurrenceResolved?: boolean;
  /** A downstream rejection to surface (race loss, closure, …). */
  conflict?: string;
  /** Chain legs already booked (advances as saves land). */
}

const patientLabel = (draft: SchedulingCommandDraft, r: CompletionResolution): string => {
  switch (r.patient) {
    case 'resolved': return `Patient — ${draft.patientQuery}`;
    case 'ambiguous': return `Which ${draft.patientQuery}?`;
    case 'none': return `No ${draft.patientQuery} here — register?`;
    default: return `Finding ${draft.patientQuery}…`;
  }
};

/**
 * Compose the rail for a draft + its current resolution.
 * Deterministic: same inputs, same sequence, always.
 */
export function composeCompletionRail(
  draft: SchedulingCommandDraft,
  r: CompletionResolution,
): CompletionCard[] {
  const cards: CompletionCard[] = [];

  cards.push({
    kind: 'request-header',
    state: 'done',
    key: 'request-header',
    label: 'From your request',
  });

  // ── Patient (always; Register replaces its ask on zero matches — rule 3) ──
  if (r.patient === 'none') {
    cards.push({ kind: 'patient', state: 'done', key: 'patient', label: patientLabel(draft, r) });
    cards.push({
      kind: 'register-patient',
      state: 'open',
      key: 'register-patient',
      label: `Register ${draft.patientQuery}`,
    });
  } else {
    cards.push({
      kind: 'patient',
      state: r.patient === 'resolved' ? 'done' : 'open',
      key: 'patient',
      label: patientLabel(draft, r),
    });
  }

  // ── Practitioner ──
  cards.push({
    kind: 'practitioner',
    state: 'pending',
    key: 'practitioner',
    label: r.practitionerResolved
      ? `Doctor — ${draft.doctorQuery ?? 'selected'}`
      : draft.doctorQuery
        ? `Doctor "${draft.doctorQuery}" — pick`
        : 'Which doctor?',
  });

  // ── Visit type (depends on doctor) ──
  cards.push({
    kind: 'visit-type',
    state: 'pending',
    key: 'visit-type',
    label: r.visitTypeResolved
      ? `Visit — ${draft.profileQuery ?? 'selected'}`
      : draft.profileQuery
        ? `Visit type "${draft.profileQuery}" — confirm`
        : 'Which visit type?',
    dependsOnDoctor: true,
  });

  // ── Time | Recurrence (mutually exclusive per draft shape — rule 3) ──
  if (draft.recurrence) {
    cards.push({
      kind: 'recurrence-dates',
      state: 'pending',
      key: 'recurrence-dates',
      label: `Repeats ${draft.recurrence.period === 'Months' ? 'monthly' : 'weekly'} ×${draft.recurrence.count} — confirm the dates`,
      dependsOnDoctor: true,
    });
  } else {
    cards.push({
      kind: 'time-slot',
      state: 'pending',
      key: 'time-slot',
      label: 'Time',
      dependsOnDoctor: true,
    });
  }

  // ── Chain history — visits of this request already booked (the frame
  //    pops booked legs out of `chain` between saves; `chainDone` keeps
  //    them visible so the journey never resets — rule 4 across saves). ──
  (draft.chainDone ?? []).forEach((label, i) => {
    cards.push({
      kind: 'chain-leg',
      state: 'done',
      key: `chain-done-${i}`,
      label,
    });
  });

  // ── Chain legs (render only when the draft chains — rule 3) ──
  // Remaining legs are by definition NOT booked — booked legs were popped
  // into chainDone by advanceAiChain and rendered as history above. A count
  // applied to the REMAINING array double-counted and showed false progress
  // (Codex P2 on 6be97d8a) — the field is deleted, chainDone is the single
  // source of chain progress.
  (draft.chain ?? []).forEach((leg, i) => {
    cards.push({
      kind: 'chain-leg',
      state: 'pending',
      key: `chain-leg-${i}`,
      label: `Then — ${leg.profileQuery ?? 'visit'}${leg.doctorQuery ? ` with ${leg.doctorQuery}` : ''}`,
      legIndex: i,
    });
  });

  // ── Conflict (only on rejection — rule 3; it takes the open slot) ──
  if (r.conflict) {
    cards.push({ kind: 'conflict', state: 'pending', key: 'conflict', label: r.conflict });
  }

  cards.push({ kind: 'receipt', state: 'pending', key: 'receipt', label: 'Defaults — self-pay · reminders on' });

  return applyOpenState(cards, r);
}

/**
 * Rule 2: resolve per-card done/pending, then exactly one open ask.
 * Precedence for the ask: patient/register (already set above) → conflict →
 * practitioner → visit type → time/recurrence. Receipt and chain legs never
 * ask — legs advance via saves, the receipt is a tap-to-expand row.
 */
function applyOpenState(cards: CompletionCard[], r: CompletionResolution): CompletionCard[] {
  const done = (k: CompletionCardKind): boolean =>
    (k === 'practitioner' && r.practitionerResolved) ||
    (k === 'visit-type' && r.visitTypeResolved) ||
    (k === 'time-slot' && r.slotResolved) ||
    (k === 'recurrence-dates' && r.recurrenceResolved === true);

  const out = cards.map((c) =>
    c.state === 'pending' && done(c.kind) ? { ...c, state: 'done' as const } : c,
  );

  if (out.some((c) => c.state === 'open')) return out; // patient/register already asking

  const askOrder: CompletionCardKind[] = ['conflict', 'practitioner', 'visit-type', 'recurrence-dates', 'time-slot'];
  for (const kind of askOrder) {
    const idx = out.findIndex((c) => c.kind === kind && c.state === 'pending');
    if (idx >= 0) {
      out[idx] = { ...out[idx], state: 'open' };
      return out;
    }
  }
  return out;
}

/** Rule 5: Book enables only when nothing is open or pending (except receipt + booked legs). */
export function bookEnabled(cards: CompletionCard[]): boolean {
  return cards.every(
    (c) =>
      c.state === 'done' ||
      c.kind === 'receipt' ||
      (c.kind === 'chain-leg' && c.state === 'pending'),
  );
}
